/**
 * The Réglages frame: the manager gate, the tab rail, the content width, and
 * nothing else.
 *
 * Every child route was its own page with its own copy of the same
 * `canManage` beforeLoad — five copies, one per page. The gate lives here now
 * and the children inherit it, which is the main reason this layout exists at
 * all rather than the rail being a component each page renders.
 *
 * The content width is the second reason. Each settings page used to pick its
 * own container and they all disagreed — `max-w-2xl p-4` here, `max-w-5xl
 * px-6 py-10` on /units. Two named widths replace the guessing: forms read
 * badly past `prose`, and the data-table tabs need `wide`. Each tab declares
 * which it wants in `lib/settings-tabs`, and this layout applies it, so a page
 * never restates a width the rail already knows.
 */
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";

import { cn } from "@gately/ui";
import { TabNav, TabNavItem } from "@gately/ui/tabs";

import { orpc } from "~/lib/orpc";
import { activeSettingsTab, buildSettingsTabs } from "~/lib/settings-tabs";
import { resolvePermissions, usePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/reglages")({
  // The single manager gate for the whole subtree. Until this layout existed,
  // each settings page carried its own identical copy.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).canManage) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: ReglagesLayout,
});

function ReglagesLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tabs = buildSettingsTabs(useTerms(), usePermissions());
  const active = activeSettingsTab(pathname, tabs);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6">
      <h1 className="text-2xl font-semibold tracking-tight">Réglages</h1>

      <div className="mt-6 flex flex-col gap-8 md:flex-row md:gap-10">
        <TabNav orientation="vertical" aria-label="Réglages">
          {tabs.map((tab) => (
            <TabNavItem key={tab.to} asChild active={active?.to === tab.to}>
              <Link to={tab.to}>
                <tab.icon />
                {tab.label}
              </Link>
            </TabNavItem>
          ))}
        </TabNav>

        {/* The width comes from the tab's own declaration in `lib/settings-tabs`,
            applied here because this is where the active tab is already known.
            Letting each page pass its own width made the rail's declaration and
            the page's choice two sources of truth that could silently disagree. */}
        <div
          className={cn(
            "min-w-0 flex-1",
            active?.width === "wide" ? "w-full" : "w-full max-w-2xl",
          )}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
}
